import styled from 'styled-components';
import themeGet from '@styled-system/theme-get';
import { useIntl } from 'gatsby-plugin-intl';

import { BladeType, useBlade } from '../state/BladeProvider';
import useKeyValuePairs from '../hooks/useKeyValuePairs';

import BladeMenu from './BladeMenu';
import Burger from './Burger';
import GlobalSearch from './SiteSearch/GlobalSearch';
import MainMenuV3 from './MainMenuV3';
import LangAndProvinceSelector from './LangAndProvinceSelector';

const MenuBtn = styled.button.attrs({ type: 'button' })`
  display: flex;
  align-items: center;
  color: black;
  padding: 0.5rem 0;
  cursor: pointer;

  p {
    margin-bottom: 0;
    margin-right: 1.25rem;
    font-size: 14px;
    font-weight: ${themeGet('fontWeights.medium')};

    @media only screen and (max-width: ${themeGet('breakpoints.md')}) {
      display: none;
    }
  }
`;

const LangAndProvinceSelectorWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  margin-bottom: 20px;
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
`;

interface MainMenuBladeProps {
  className?: string;
  title: string;
  closeMsg: string;
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  menu: any;
}

const MainMenuBlade = ({
  className,
  title,
  closeMsg,
  menu,
}: MainMenuBladeProps) => {
  const { activeBlade, setActiveBlade } = useBlade();
  const intl = useIntl();
  const keyValuePairs = useKeyValuePairs(intl.locale);

  const isMenuBladeOpen = () => activeBlade === BladeType.MENU;
  const updateBladeState = () => setActiveBlade(isMenuBladeOpen() ? null : BladeType.MENU);
  const resetBladeState = () => setActiveBlade(null);
  const bladeState = activeBlade === BladeType.MENU;

  const closeBladeOnEsc = (e: React.KeyboardEvent) => {
    const event = e || window.event;
    if (event.key === 'Escape' || event.keyCode === 27) {
      resetBladeState();
    }
  };

  return (
    <>
      <MenuBtn
        className={className}
        onClick={() => updateBladeState()}
        aria-label={title}
        aria-haspopup="true"
        aria-expanded={bladeState}
      >
        <Burger />
      </MenuBtn>
      <BladeMenu
        title={title}
        closeMsg={closeMsg}
        isOpen={bladeState}
        action={updateBladeState}
        closeAllMenusOnEsc={closeBladeOnEsc}
        resetState={resetBladeState}
        ctas={menu.bladeCTAs}
      >
        <MainMenuV3 data={menu} />
        <LangAndProvinceSelectorWrapper>
          <LangAndProvinceSelector />
        </LangAndProvinceSelectorWrapper>
        <GlobalSearch
          id="main-menu-search"
          type="main-menu is-open"
          placeholder={keyValuePairs.SEARCH_PLACEHOLDER}
        />
      </BladeMenu>
    </>
  );
};

export default MainMenuBlade;
